import React, { useState, useCallback } from 'react'

import { NewLocation } from '.'
import ConfirmCancelButtons from 'components/Buttons/ConfirmCancelButtons'
import {
	Container,
	Header,
	StreetAddressInput,
	InputRow,
	ProvinceInput,
	ZipCodeInput,
	UnitInput,
	PhoneInput,
	HorizontalSeparator,
	SelectInput,
	FieldError,
} from './AddOfficeLocation.styles'

interface AddOfficeLocationProps {
	onAdd: (location: NewLocation) => any,
	onCancel: () => any,
}

export default function AddOfficeLocation(
	{
		onAdd,
		onCancel,
	}: AddOfficeLocationProps,
) {

	const [location, setLocation] = useState({
		streetAddress: '',
		province: '',
		zipCode: '',
		unit: '',
		email: '',
		scentFreePolicy: '',
	} as NewLocation)
	const [errors, setErrors] = useState<Record<string, string>>({})
	const editLocation = useCallback((newState: any) => {
		setLocation({
			...location,
			...newState,
		})
		const keys = Object.keys(newState)
		if (keys.length) {
			setErrors((prev) => {
				const next = { ...prev }
				keys.forEach((k) => { delete next[k] })
				return next
			})
		}
	}, [location, setLocation])

	const scentPolicyOptions = [
		{ value: '', label: 'Select scent-free policy' },
		{ value: 'scent-free', label: 'Scent-free' },
		{ value: 'not-scent-free', label: 'Not scent-free' },
	];

	const handleAdd = () => {
		const newErrors: Record<string, string> = {}
		if (!location.streetAddress.trim()) newErrors.streetAddress = 'Street address is required'
		if (!location.province.trim()) newErrors.province = 'Province / Territory is required'
		if (!location.zipCode.trim()) newErrors.zipCode = 'Postal/ZIP Code is required'
		if (!location.unit.trim()) newErrors.unit = 'Unit # is required'
		if (!location.email.trim()) newErrors.email = 'Email is required'
		if (!location.scentFreePolicy) newErrors.scentFreePolicy = 'Please select a scent-free policy'

		if (Object.keys(newErrors).length) {
			setErrors(newErrors)
			return
		}
		setErrors({})
		onAdd(location)
	}

	return (
		<Container>
			<Header>
				Add Office Location
			</Header>
			<p className='info'>
				Enter the address of your new office location.
			</p>
			<StreetAddressInput
				placeholder={'Street Address'}
				style={errors.streetAddress ? { borderColor: '#FB7672' } : undefined}
				onChange={({ target }) => {
					editLocation({ streetAddress: target.value })
				}}
			/>
			{errors.streetAddress && <FieldError>{errors.streetAddress}</FieldError>}
			<InputRow>
				<div style={{ flex: 0.56, marginRight: 6 }}>
					<ProvinceInput
						placeholder={'Province / Territory'}
						style={errors.province ? { borderColor: '#FB7672' } : undefined}
						onChange={({ target }) => {
							editLocation({ province: target.value })
						}}
					/>
					{errors.province && <FieldError>{errors.province}</FieldError>}
				</div>
				<div style={{ flex: 0.44, marginLeft: 6 }}>
					<ZipCodeInput
						placeholder={'Postal/ZIP Code'}
						style={errors.zipCode ? { borderColor: '#FB7672' } : undefined}
						onChange={({ target }) => {
							editLocation({ zipCode: target.value })
						}}
					/>
					{errors.zipCode && <FieldError>{errors.zipCode}</FieldError>}
				</div>
			</InputRow>
			<InputRow>
				<div style={{ flex: 0.33, marginRight: 6 }}>
					<UnitInput
						placeholder={'Unit #'}
						style={errors.unit ? { borderColor: '#FB7672' } : undefined}
						onChange={({ target }) => {
							editLocation({ unit: target.value })
						}}
					/>
					{errors.unit && <FieldError>{errors.unit}</FieldError>}
				</div>
				<div style={{ flex: 0.67, marginLeft: 6 }}>
					<PhoneInput
						placeholder={'Email'}
						style={errors.email ? { borderColor: '#FB7672' } : undefined}
						onChange={({ target }) => {
							editLocation({ email: target.value })
						}}
					/>
					{errors.email && <FieldError>{errors.email}</FieldError>}
				</div>
			</InputRow>
			<HorizontalSeparator />
			<label style={{ fontWeight: 600, marginBottom: 6 }}>Scent-Free Policy</label>
			<SelectInput
				value={location.scentFreePolicy}
				style={errors.scentFreePolicy ? { borderColor: '#FB7672' } : undefined}
				onChange={({ target }) => {
					setErrors((prev) => { const next = { ...prev }; delete next.scentFreePolicy; return next })
					editLocation({ scentFreePolicy: target.value });
				}}
			>
				{scentPolicyOptions.map(option => (
					<option key={option.value} value={option.value}>{option.label}</option>
				))}
			</SelectInput>
			{errors.scentFreePolicy && <FieldError>{errors.scentFreePolicy}</FieldError>}
			<ConfirmCancelButtons
				confirmText={'Add'}
				onConfirm={handleAdd}
				cancelText={'Cancel'}
				onCancel={onCancel}
			/>
		</Container>
	)
}
